
import { DefaultEnums, GamePlayState } from "../Enums/GameEnums";
import { MONSTER_CLASSES } from '../Enums/Attributes';
import { setLocalStorage } from '../helpers/LocalStorage';
import { GameState, blackjackEngineWrapper, initialiseNewBJ } from './gameSlice';
import { LocationNames, LocationAdjectivesOne, LocationAdjectivesTwo, MonsterAdjectives, SpriteGenderNames } from '../Enums/NameEnums';
import { sendRequest } from "../api/axios-request";
import { PLAYER_STATS_EVENT_NAME, appInsights } from "../api/appInsights";
import { randomEnum, returnRandomElementOfArray } from "../helpers/helpers";
import { bags } from "../helpers/BagSystem";
import { addKilledProgress, addBlackJackProgress, addDamageBlocked } from "./achievementLogic";

export function dealDamageToPlayer(state, isBlackjack: boolean = false) {
  const multiplier = isBlackjack ? 2 : 1;
  const attemptedDamage = ((state.bet + state.monster.attributes.att) * multiplier)
  const damage = penetrationDamage(state, state.player.attributes.def, DefaultEnums.DEFAULT_PENETRATION_MODIFIER * state.monster.attributes.pierce, attemptedDamage, false);
  if (damage > 0) {

    if (!state.isMuted) {
      const audio = new Audio(`BlackJack RPG_${state.player.gender}_Pain SFX_20.10.2023.mp3`);
      audio.play();
    }

    if (isBlackjack) {
      state.progressData = addBlackJackProgress(state, damage, false);
      state.popup.contentTop = state.monster.namePrefix + ' scored BJ and dealt ' + damage + ' critical damage to you';
    } else {
      state.popup.contentTop = state.monster.namePrefix + ' dealt ' + damage + ' damage to you';
    }
    state.popup.contentBottom = 'You Scored ' + getScore(state.engineState.handInfo.right.playerValue.hi) + ` vs Monster's ` + getScore(state.engineState.dealerValue.hi);
    state.popup.isVisible = true
    const updatedHp = state.player.attributes.currentHp - damage;
    if (updatedHp > 0) {
      state.player.attributes.currentHp = updatedHp;
      state.gamePlayState = GamePlayState.BETTING;
    } else {

      state.progressData = addKilledProgress(state, false);
      death(state);

      saveStats(state);

      state.gamePlayState = GamePlayState.DEFEAT;
      setLocalStorage(state);
    }
  } else {
    state.popup.contentTop = 'Luckily monsters damage was too low to go through your defense';
    state.popup.isVisible = true;
    state.gamePlayState = GamePlayState.BETTING;
  }
  blackjackEngineWrapper.newInstance();
  state.engineState = blackjackEngineWrapper.getEngineState()
};

export function dealDamageToMonster(state: GameState, isBlackjack: boolean = false) {
  const multiplier = isBlackjack ? 2 : 1;
  const attemptedDamage = ((state.bet + state.player.attributes.att) * multiplier)
  const damage = penetrationDamage(state, state.monster.attributes.def, DefaultEnums.DEFAULT_PENETRATION_MODIFIER * state.player.attributes.pierce, attemptedDamage, true);
  if (damage > 0) {

    if (!state.isMuted) {
      const audio = new Audio('BlackJack RPG_Hit2 SFX_20.10.2023.mp3');
      audio.play();
    }

    if (isBlackjack) {
      state.progressData = addBlackJackProgress(state, damage, true);
      state.popup.contentTop = 'You scored BJ and dealt ' + damage + ' critical damage to ' + state.monster.namePrefix;
    } else {
      state.popup.contentTop = 'You dealt ' + damage + ' damage to ' + state.monster.namePrefix;
    }
    state.popup.contentBottom = 'You Scored ' + getScore(state.engineState.handInfo.right.playerValue.hi) + ` vs Monster's ` + getScore(state.engineState.dealerValue.hi);
    state.popup.isVisible = true;
    const updatedHp = state.monster.attributes.currentHp - damage;
    const isMonsterBoss = MONSTER_CLASSES.find(x => x.name === state.monster.name).boss;
    if (updatedHp > 0) {
      state.monster.attributes.currentHp = updatedHp;
      state.gamePlayState = GamePlayState.BETTING;
    } else {
      state.progressData = addKilledProgress(state, true);
      state.player.monstersBeaten++;
      if (state.player) {
        saveStats(state);
      }
      state.stage++;
      if (isMonsterBoss) {
        state.gamePlayState = GamePlayState.VICTORY;
        state.popup.contentTop = '';
        state.popup.isVisible = false;
      } else {
        state.monster.isVisible = false;
        spawnNewMonster(state);
        state.gamePlayState = GamePlayState.BETTING;
      }
    }
  } else {
    state.popup.contentTop = 'Sadly your damage was too low to go through the enemy defense';
    state.popup.isVisible = true;
    state.gamePlayState = GamePlayState.BETTING;
  }
  state.balance = state.balance + state.engineState.wonOnRight;
  setLocalStorage(state);
};

function getScore(num: number) {
  return num < 22 ? num : 'Bust';
}

export function getFullName(name: string, gender: SpriteGenderNames) {
  const prefix = gender === SpriteGenderNames.M ? 'Lord' : 'Lady';
  return `${prefix} ${name}`;
}

export function penetrationDamage(state, defense: number, pierce: number, damage: number, isPlayer: boolean) {
  let result = 0;
  let blockedDamage = 0;
  if (defense > 0 && pierce >= 0) {
    result = defense - pierce;
    const blockedAll = result >= damage
    blockedDamage = blockedAll ? damage : defense;
    state.progressData = addDamageBlocked(state, blockedDamage, blockedAll, isPlayer);
    if (result < 0) {
      result = 0
    }
  } else if (defense < 0) {
    result = defense - pierce;
  }
  return damage - result
}

export function spawnNewMonster(state) {
  const isBoss = state.stage === DefaultEnums.NUMBER_OF_STAGES;

  const selectedMonster = isBoss
    ? bags.bosses.getNext()
    : bags.monsters.getNext();

  const level = state.level;
  const stage = state.stage;
  const scalingFactor = level * (1 + (level > 2 ? 0.5 : 0) + (level > 5 ? 0.5 : 0) + (level > 15 ? 1 : 0) + (level > 50 ? 1 : 0));
  const attack = isBoss ? selectedMonster.att * scalingFactor : selectedMonster.att + (stage * (scalingFactor / 2));
  const defense = isBoss ? (selectedMonster.def * scalingFactor) + 1 : selectedMonster.def + ((scalingFactor / 2));
  const pierce = isBoss ? (selectedMonster.pierce * scalingFactor) - 1 : selectedMonster.pierce + ((scalingFactor / 2) - 1)
  let hp = ((selectedMonster.hp + stage) * scalingFactor) + (isBoss ? (2 * scalingFactor) : 0);
  if (hp < 0) {
    hp = 1;
  }
  const monster = {
    name: selectedMonster.name,
    attributes: {
      att: roundAttribute(attack),
      def: roundAttribute(defense),
      pierce: roundAttribute(pierce),
      maxHp: roundAttribute(hp),
      currentHp: roundAttribute(hp),
    },
    namePrefix: returnRandomElementOfArray(MonsterAdjectives) + ' ' + selectedMonster.name
  }
  state.monster = Object.assign(state.monster, { ...monster });
  state.popup.isVisible = true
};

function roundAttribute(attribute: number) {
  const rounded = Math.round(attribute);
  if (rounded < 0) {
    return 0;
  }
  return rounded;
}
export function generateNewLocation(state) {
  const name = randomEnum(LocationNames);
  const levelFullName = ' ' + returnRandomElementOfArray(LocationAdjectivesOne) + ' ' + returnRandomElementOfArray(LocationAdjectivesTwo) + ' ' + name;
  state.levelName = name;
  state.levelFullName = levelFullName;
  state.level++;
  state.stage = 1;
  state.rewardIndex = 0;
};

export function resetState(state: GameState) {
  state.level = 0;
  state.stage = 1;

  if (state.player) {

    if (state.player.attributes && state.player.attributes.maxHp) {
      state.player.attributes.currentHp = state.player.attributes.maxHp;
    }

    state.player.healthPotionCount = DefaultEnums.DEFAULT_POTIONS;
  }

  state.popup.contentTop = '';
  state.popup.isVisible = false;
  state.storyNumber = 1;

  initialiseNewBJ(Object.assign({}, state));
}

export function isPlayerValue21(state) {
  if (state.engineState.handInfo?.right?.playerValue?.hi === 21 || state.engineState.handInfo?.right?.playerValue?.lo === 21) {
    return true;
  } else {
    return false;
  }
}

export function death(state) {
  state.player.numberOfDeaths++;
  resetState(state);
  generateNewLocation(state);
  spawnNewMonster(state);
}

export function isMonsterValueBJ(state) {
  if (state.engineState.dealerHasBlackjack) {
    return true;
  } else {
    return false;
  }

}

export function isPlayerValueBJ(state) {
  if (state.engineState.handInfo.right.playerHasBlackjack) {
    return true;
  } else {
    return false;
  }
}

function saveStats(state: GameState) {

  const stats = {
    name: getFullName(state.player.name, state.player.gender),
    progressData: state.progressData,
    level: state.player.maxLevelReached,
    numberOfDeaths: state.player.numberOfDeaths,
    monsters: state.player.monstersBeaten,
    userId: state.userId,
    balance: state.balance,
    bet: state.bet,
    potions: state.player.healthPotionCount,
    startTime: state.startTime || null,
    timePlayed: state.timePlayed || 0
  };

  // save to our scoreboard backend
  sendRequest(stats);

  // save to app insights
  appInsights.trackEvent({ name: PLAYER_STATS_EVENT_NAME, properties: stats });

}
